/**
 * Le branchement du bruitage sur la partie.
 *
 * Tout le raisonnement est ailleurs : `annonces.ts` dit ce qui se passe,
 * `partition.ts` ce que ça doit produire, `bruiteur.ts` comment le synthétiser,
 * `haptique.ts` comment le faire sentir. Ici, du React et rien d'autre.
 *
 * ## Pourquoi ce hook refait la comparaison d'états
 *
 * `useAnnonces` la fait déjà, mais il rend ce qui est **visible** — une file bornée à
 * trois, qui vieillit. Le bruitage, lui, veut les annonces **neuves**, une seule fois
 * chacune. Plutôt que de tordre l'API du bandeau pour lui faire rendre deux choses,
 * on rappelle `annoncesEntre`, qui est pure et coûte un parcours de tableau. Les deux
 * hooks restent indépendants, et aucun ne peut casser l'autre.
 *
 * ⚠️ La comparaison porte sur la **révision**, pas sur l'identité de l'objet : en
 * ligne l'état est reconstruit à chaque patch, donc toujours « nouveau ». Comparer les
 * références ferait sonner la cloche du 421 plusieurs fois par seconde.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";

import type { Partie } from "@zinc421/shared";

import { textesDe } from "../i18n/index.js";
import { annoncesEntre } from "../jeu/annonces.js";
import { creerBruiteur, type Bruiteur } from "./bruiteur.js";
import { vibrer, couperHaptique } from "./haptique.js";
import {
  effetDeGarde, effetDeLancer, effetDuChoc, effetsDesAnnonces, type Effet,
} from "./partition.js";

/**
 * Un seul bruiteur pour toute l'application.
 *
 * Un contexte audio par écran finirait par en ouvrir une douzaine : iOS en limite le
 * nombre et refuse silencieusement les suivants — le jeu deviendrait muet au bout de
 * quelques parties, sans aucune erreur.
 */
let unique: Bruiteur | null = null;

function bruiteur(): Bruiteur {
  unique ??= creerBruiteur();
  return unique;
}

export interface Bruitage {
  /** Un dé vient de toucher, avec la force du choc. */
  readonly surChoc: (force: number) => void;
  /** Un dé vient d'être mis de côté ou rendu au jeu. */
  readonly surGarde: (garde: boolean) => void;
  /** Le joueur vient de lancer, avec le nombre de dés jetés. */
  readonly surLancer: (des: number) => void;
}

/**
 * Fait sonner une partie.
 *
 * `actif` commande le son **et** l'haptique : quelqu'un qui coupe le son dans un bar
 * veut être discret, pas seulement silencieux.
 */
export function useBruitage(
  partie: Partie, monSiege: number | undefined, actif: boolean,
): Bruitage {
  const precedent = useRef<Partie | null>(null);
  const derniereRevision = useRef<number>(-1);
  const enMarche = useRef(actif);
  enMarche.current = actif;

  const rendre = useCallback((effet: Effet) => {
    if (!enMarche.current) return;
    bruiteur().jouer(effet);
    vibrer(effet.vibration);
  }, []);

  useEffect(() => {
    bruiteur().reglerVolume(actif ? 1 : 0);
    if (!actif) couperHaptique();
  }, [actif]);

  /**
   * Le déblocage iOS : rien ne peut sonner avant un geste de l'utilisateur, et le
   * geste doit être capté au niveau du document — le premier clic du joueur porte
   * souvent sur un bouton dont le gestionnaire, lui, déclenche déjà un son.
   */
  useEffect(() => {
    const reveil = () => { bruiteur().reveiller(); };
    const options = { capture: true, passive: true } as const;
    document.addEventListener("pointerdown", reveil, options);
    document.addEventListener("keydown", reveil, options);
    return () => {
      document.removeEventListener("pointerdown", reveil, options);
      document.removeEventListener("keydown", reveil, options);
    };
  }, []);

  /**
   * Cycle de vie mobile (§ 5.4) : le son se tait en arrière-plan.
   *
   * Ce n'est pas une politesse. iOS coupe la session audio d'une page cachée, et un
   * contexte laissé tourner ne revient pas : le jeu resterait muet pour le reste de
   * la partie, sans la moindre erreur en console.
   */
  useEffect(() => {
    const auChangement = () => {
      if (document.visibilityState === "visible") bruiteur().reveiller();
      else { bruiteur().suspendre(); couperHaptique(); }
    };
    document.addEventListener("visibilitychange", auChangement);
    return () => { document.removeEventListener("visibilitychange", auChangement); };
  }, []);

  useEffect(() => {
    if (partie.revision === derniereRevision.current) return;
    const avant = precedent.current;
    derniereRevision.current = partie.revision;
    precedent.current = partie;
    if (!avant) return;   // le premier état ne sonne pas : il n'y a pas d'avant

    // ⚠️ Un dictionnaire fixe, et c'est volontaire : le bruitage ne lit que le MOTIF
    // des annonces, jamais leur texte. Le faire dépendre de la langue rendrait le son
    // sensible à une traduction, pour un résultat rigoureusement identique.
    const annonces = annoncesEntre(avant, partie, textesDe("fr"), monSiege);
    for (const effet of effetsDesAnnonces(annonces)) {
      rendre(effet);
    }
  }, [partie, monSiege, rendre]);

  return useMemo<Bruitage>(() => ({
    surChoc: (force) => { rendre(effetDuChoc({ seconde: 0, force }, 0)); },
    surGarde: (garde) => { rendre(effetDeGarde(garde)); },
    surLancer: (des) => { rendre(effetDeLancer(des)); },
  }), [rendre]);
}
